/**
 * TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN
 * =============================================
 * Landing page for the verification email link.
 *
 * URL:  /#/onboarding/verify-email?token=…
 *
 * Contract:
 *   • This page NEVER redirects to Login.
 *   • It calls POST /api/onboarding/verify-email once with the token.
 *   • On success it broadcasts a `treatx-onboarding-email-verified`
 *     event via BroadcastChannel + localStorage change so the ORIGINAL
 *     browser tab (which is polling the session status) can pick up
 *     the change instantly on same-device, and via storage on same-
 *     browser cross-tab.
 *   • Then it shows a static "You can close this tab" success screen.
 *   • Errors show a redemption-friendly message + link to /login for
 *     users who genuinely arrived here without a live session.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { verifyEmailToken } from '../../utils/onboardingSession';

const BROADCAST_CHANNEL = 'treatx-onboarding';
const STORAGE_PING_KEY = 'treatx.onboarding.emailVerifiedPing';

const broadcastVerified = (payload) => {
  // 1. Same-device / same-browser cross-tab via BroadcastChannel.
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(BROADCAST_CHANNEL);
      ch.postMessage({ type: 'email_verified', ...payload });
      // Close after a tick so listeners have time to receive.
      setTimeout(() => ch.close(), 100);
    }
  } catch { /* ignore */ }
  // 2. Fallback: storage event on same-origin tabs.
  try {
    localStorage.setItem(STORAGE_PING_KEY, JSON.stringify({
      at: new Date().toISOString(),
      ...payload,
    }));
  } catch { /* ignore */ }
};

export default function VerifyEmailLandingPage() {
  const { t } = useTranslation();
  const [state, setState] = useState('verifying'); // verifying | verified | invalid | expired | error
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    // Read the token from the hash-router query string.
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const q = hash.includes('?') ? hash.split('?')[1] : '';
    const params = new URLSearchParams(q);
    const token = params.get('token');
    if (!token) {
      setState('invalid');
      setDetail('No verification token found in the link.');
      return;
    }
    // Owner §4: the one-time verification token may exist in the URL only long
    // enough to perform this deliberate exchange. Strip it immediately (before
    // the network round-trip) so it never persists in history/back-forward or
    // is shoulder-surfed. The token var above already holds the value.
    try {
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        const base = (window.location.hash || '').split('?')[0] || '#/onboarding/verify-email';
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${base}`);
      }
    } catch { /* ignore */ }
    (async () => {
      try {
        const out = await verifyEmailToken(token);
        setDetail({
          email: out.email,
          role: out.role,
          verified_at: out.verified_at,
        });
        broadcastVerified({
          session_id: out.session_id,
          email: out.email,
          role: out.role,
        });
        setState('verified');
      } catch (err) {
        // FastAPI HTTPException detail can be either a string OR a
        // structured object (list of validation errors). Coerce
        // safely — never call String methods on non-strings.
        const rawDetail = err?.detail;
        const code = (typeof rawDetail === 'string' ? rawDetail : '').toUpperCase();
        if (code.includes('EXPIRED')) {
          setState('expired');
          setDetail('This verification link has expired. Restart signup from your original browser to receive a fresh link.');
        } else if (code.includes('INVALID')) {
          setState('invalid');
          setDetail('This verification link is not valid. It may have already been used.');
        } else if (err?.status === 400) {
          // Pydantic validation error (malformed / truncated token).
          // The raw JSON payload is not user-facing — surface a
          // friendly message and steer to restart.
          setState('invalid');
          setDetail('This verification link is not valid. It may have been truncated by an email client — restart signup to receive a fresh link.');
        } else {
          setState('error');
          setDetail('Could not verify your email right now. Please return to the original tab and re-send the verification email.');
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="verify-email-landing"
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: 'radial-gradient(circle at 30% 20%, #0c1f2c 0%, #050a10 60%, #02050a 100%)', color: '#e8eef7' }}
    >
      <div className="max-w-lg w-full rounded-2xl p-8 border" style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(14px)' }}>
        {state === 'verifying' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
              <h1 className="text-xl font-semibold">{t('patient.verifyEmail.verifying')}</h1>
            </div>
            <p className="text-sm text-slate-300">{t('patient.common.loading')}</p>
          </>
        )}

        {state === 'verified' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="h-7 w-7 text-emerald-400" />
              <h1 className="text-2xl font-semibold" data-testid="verify-email-success-heading">{t('patient.verifyEmail.successTitle')}</h1>
            </div>
            <p className="text-sm text-slate-200 mb-3">
              <span className="font-mono text-emerald-300">{detail?.email}</span>
            </p>
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100 mb-4" data-testid="verify-email-return-hint">
              <strong className="block mb-1">{t('patient.verifyEmail.successBody')}</strong>
            </div>
          </>
        )}

        {(state === 'invalid' || state === 'expired' || state === 'error') && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="h-7 w-7 text-rose-400" />
              <h1 className="text-2xl font-semibold" data-testid="verify-email-failure-heading">
                {state === 'expired' ? t('patient.verifyEmail.expiredTitle') : state === 'invalid' ? t('patient.verifyEmail.failedTitle') : t('patient.verifyEmail.failedTitle')}
              </h1>
            </div>
            <p className="text-sm text-slate-200 mb-4" data-testid="verify-email-failure-detail">
              {state === 'expired' ? t('patient.verifyEmail.expiredBody') : t('patient.verifyEmail.failedBody')}
            </p>
            <p className="text-xs text-slate-400">
              {t('patient.verifyEmail.requestNew')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
